import { useEffect, useState } from "react";
import { usePostHog } from "posthog-js/react";
import { useLocation, useNavigate } from "react-router-dom";

import { captureAnalyticsEvent } from "../utils/analytics";
import { PH_EVENTS_WEB } from "../utils/analyticsEvents";
import { authClient } from "../utils/auth-client";

export default function EmailVerified() {
  console.log("🚀 EmailVerified component mounted!");
  console.log("🚀 Current URL:", window.location.href);
  console.log("🚀 Location pathname:", window.location.pathname);
  console.log("🚀 Location search:", window.location.search);

  const location = useLocation();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [redirectMessage, setRedirectMessage] = useState("");

  useEffect(() => {
    const handleVerificationRedirect = async () => {
      console.log("🔍 EmailVerified useEffect triggered");
      console.log("🔍 location.search:", location.search);

      const searchParams = new URLSearchParams(location.search);
      const authMode = searchParams.get("authMode");
      const purchaseData = searchParams.get("purchaseData");
      const authProviderFromQuery = searchParams.get("authProvider");
      const intentAuthModeFromQuery = searchParams.get("intentAuthMode");

      // Decode purchase flow parameters from single encoded parameter
      let fromPurchaseFlow = "false";
      let stage = null;
      let product = null;
      let contractType = null;
      let authProvider: string | null = authProviderFromQuery;
      let intentAuthMode: string | null = intentAuthModeFromQuery;

      console.log("🔍 Raw purchaseData parameter:", purchaseData);

      if (purchaseData) {
        try {
          console.log("🔍 Attempting to decode purchaseData...");
          const decodedJson = atob(purchaseData);
          console.log("🔍 Decoded JSON string:", decodedJson);
          const decodedData = JSON.parse(decodedJson);
          console.log("🔍 Parsed purchase data:", decodedData);

          fromPurchaseFlow = decodedData.fromPurchaseFlow || "false";
          stage = decodedData.stage;
          product = decodedData.product;
          contractType = decodedData.contractType;
          if (decodedData.authProvider) {
            authProvider = decodedData.authProvider;
          }
          if (decodedData.intentAuthMode) {
            intentAuthMode = decodedData.intentAuthMode;
          }

          console.log("🔍 Extracted values:", {
            fromPurchaseFlow,
            stage,
            product,
            contractType,
          });
        } catch (error) {
          console.error("❌ Failed to decode purchase data:", error);
          console.error("❌ Purchase data was:", purchaseData);
        }
      } else {
        console.log("🔍 No purchaseData parameter found");
      }

      console.log("🔍 URL parameters:", {
        fromPurchaseFlow,
        authMode,
        stage,
        product,
        contractType,
      });

      if (fromPurchaseFlow === "true") {
        // Purchase flow parameters already extracted above

        // Check if user is now authenticated after email verification
        try {
          setRedirectMessage("Checking authentication status...");
          console.log(
            "🔍 EmailVerified: Checking session after email verification...",
          );

          const session = await authClient.getSession();
          console.log("🔍 EmailVerified: Session result:", session);
          console.log(
            "🔍 EmailVerified: User authenticated:",
            !!session?.data?.user,
          );
          console.log("🔍 EmailVerified: Full session data:", session?.data);
          console.log("🔍 EmailVerified: Purchase flow params:", {
            stage,
            product,
            contractType,
            authProvider,
            intentAuthMode,
          });

          if (session?.data?.user) {
            // User is authenticated! Calculate checkout route and redirect directly
            console.log(
              "✅ EmailVerified: User is authenticated! Redirecting to checkout...",
            );
            setRedirectMessage(
              "Authentication successful! Redirecting to checkout...",
            );

            // Capture signup completion event for purchase flow with improved timing
            const eventCaptured = await captureAnalyticsEvent(
              posthog,
              intentAuthMode === "login"
                ? PH_EVENTS_WEB.LOGIN_COMPLETED_DURING_CHECKOUT
                : PH_EVENTS_WEB.SIGNUP_COMPLETED_DURING_CHECKOUT,
              {
                product: product,
                contractType: contractType,
                source: "email_verification",
                auth_provider: authProvider || "unknown",
                user_authenticated: true,
                auth_mode: intentAuthMode || "unknown",
              },
              true, // Wait for queue before navigation
            );

            const checkoutRoute = getCheckoutRoute(product, contractType);
            console.log(
              "🔍 EmailVerified: Calculated checkout route:",
              checkoutRoute,
            );
            console.log("🔍 EmailVerified: getCheckoutRoute inputs:", {
              product,
              contractType,
            });
            console.log("🔍 EmailVerified: Event captured:", eventCaptured);

            // Ensure sufficient time for event transmission before navigation
            setTimeout(
              () => {
                navigate(checkoutRoute);
              },
              eventCaptured ? 1000 : 1500,
            );
          } else {
            // User is not authenticated yet, fallback to login flow
            console.log(
              "❌ EmailVerified: User not authenticated, falling back to login flow",
            );
            console.log("🔍 EmailVerified: Session was:", session);
            setRedirectMessage("Redirecting to complete sign in...");

            const redirectParams = new URLSearchParams();
            if (stage) redirectParams.set("stage", stage);
            if (product) redirectParams.set("product", product);
            if (contractType) redirectParams.set("contractType", contractType);
            redirectParams.set("authMode", "login");

            const redirectUrl = `/purchase?${redirectParams.toString()}`;
            console.log("🔍 EmailVerified: Redirecting to login:", redirectUrl);

            setTimeout(() => {
              navigate(redirectUrl);
            }, 1500);
          }
        } catch (error) {
          console.error("Error checking authentication status:", error);
          // Fallback to login flow
          setRedirectMessage("Redirecting to complete sign in...");

          const redirectParams = new URLSearchParams();
          if (stage) redirectParams.set("stage", stage);
          if (product) redirectParams.set("product", product);
          if (contractType) redirectParams.set("contractType", contractType);
          redirectParams.set("authMode", "login");

          const redirectUrl = `/purchase?${redirectParams.toString()}`;

          setTimeout(() => {
            navigate(redirectUrl);
          }, 1500);
        } finally {
          setCheckingAuth(false);
        }
      } else if (authMode === "login") {
        // Regular auth flow - check if authenticated, otherwise redirect to auth page
        try {
          setRedirectMessage("Checking authentication status...");
          const session = await authClient.getSession();

          if (session?.data?.user) {
            // User is authenticated, redirect to main app
            setRedirectMessage(
              "Authentication successful! Redirecting to app...",
            );
            setTimeout(() => {
              navigate("/");
            }, 1500);
          } else {
            // Not authenticated, redirect to app root; AuthProvider will route to login with redirect
            setRedirectMessage("Redirecting to app...");
            setTimeout(() => {
              navigate("/");
            }, 1500);
          }
        } catch (error) {
          console.error("Error checking authentication status:", error);
          // Fallback to app root; AuthProvider will route as needed
          setRedirectMessage("Redirecting to app...");
          setTimeout(() => {
            navigate("/");
          }, 1500);
        } finally {
          setCheckingAuth(false);
        }
      } else {
        // No specific flow (e.g., signed up via /auth). Redirect to app root.
        setRedirectMessage("Redirecting to app...");
        setTimeout(() => {
          navigate("/");
        }, 1500);
        setCheckingAuth(false);
      }
    };

    handleVerificationRedirect();
  }, [location.search, navigate]);

  // Helper function to determine checkout route based on product and contract type
  const getCheckoutRoute = (
    product: string | null,
    contractType: string | null,
  ) => {
    console.log("🔍 getCheckoutRoute called with:", { product, contractType });

    if (product === "nettle") {
      if (contractType === "monthly") {
        return `/checkout/subscription/${encodeURIComponent("$sn_nettle_12_mo")}`;
      } else if (contractType === "onceOff") {
        return `/checkout/product/nettle`;
      }
    } else if (product === "membershipOnly") {
      if (contractType === "annual") {
        return `/checkout/subscription/${encodeURIComponent("$rc_annual")}`;
      } else if (contractType === "monthly") {
        return `/checkout/subscription/${encodeURIComponent("$rc_monthly")}`;
      }
    }

    // Fallback to purchase page to restart the flow
    return "/purchase";
  };

  const searchParams = new URLSearchParams(location.search);
  const fromPurchaseFlow = searchParams.get("fromPurchaseFlow");
  const authMode = searchParams.get("authMode");

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
        <div className="mb-6">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <svg
              className="h-6 w-6 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Email Verified Successfully!
          </h1>
          <p className="text-gray-600">
            {fromPurchaseFlow === "true"
              ? "Your email has been verified. You can now complete your purchase."
              : authMode === "login"
                ? "Your email has been verified. You can now sign in to your account."
                : "Your email has been verified. You can return to the Samphire app."}
          </p>
        </div>

        {(fromPurchaseFlow === "true" ||
          authMode === "login" ||
          checkingAuth ||
          Boolean(redirectMessage)) && (
          <div className="mt-4 text-sm text-gray-500">
            {checkingAuth
              ? "Verifying your account..."
              : redirectMessage ||
                (fromPurchaseFlow === "true"
                  ? "Redirecting you back to complete your order..."
                  : "Redirecting you to the app...")}
          </div>
        )}
      </div>
    </div>
  );
}
